import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StripeFactoryService, StripeInstance } from 'ngx-stripe';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/debounceTime';

declare var Penmate: any;

export interface StripeAddressValue {
  name?: string;
  firstName?: string;
  lastName?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
  // Additional properties for alternate naming convention
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface StripeAddressChangeEvent {
  complete: boolean;
  value: StripeAddressValue;
}

@Component({
  selector: 'app-stripe-address',
  templateUrl: './stripe-address.component.html',
  styleUrls: ['./stripe-address.component.scss']
})
export class StripeAddressComponent implements OnInit, OnDestroy {
  @ViewChild('stripeAddressElement', { static: false }) stripeAddressElement: ElementRef;
  @Input() defaultAddress: StripeAddressValue = {};
  @Input() debounceTime = 300; // Allow customization of debounce time
  @Output() addressChange = new EventEmitter<StripeAddressChangeEvent>();

  public nameValidationError = ''; // Store validation error message for template

  private stripe: StripeInstance;
  private addressElement: any;
  private addressChangeSubject = new Subject<any>();
  private addressChangeSubscription: Subscription;
  private _isValid: boolean = false;

  constructor(
    private stripeFactory: StripeFactoryService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Get required fields for a specific country
   * @param country Country code (ISO 3166-1 alpha-2)
   * @returns Object containing required field flags
   */
  private getCountryRequiredFields(country: string): {[key: string]: boolean} {
    const countryCode = country.toUpperCase();
    
    // Country-specific address requirements
    const requirements = {
      'US': { state: true, postalCode: true },
      'CA': { state: true, postalCode: true },
      'AU': { state: true, postalCode: true },
      'GB': { state: false, postalCode: true },
      'DE': { state: false, postalCode: true },
      'FR': { state: false, postalCode: true },
      'IT': { state: false, postalCode: true },
      'ES': { state: false, postalCode: true },
      'NL': { state: false, postalCode: true },
      'SE': { state: false, postalCode: true },
      'NO': { state: false, postalCode: true },
      'DK': { state: false, postalCode: true },
      'FI': { state: false, postalCode: true },
      'IE': { state: false, postalCode: true },
      'AT': { state: false, postalCode: true },
      'BE': { state: false, postalCode: true },
      'PT': { state: false, postalCode: true },
      'JP': { state: true, postalCode: true },
      'KR': { state: false, postalCode: true },
      'SG': { state: false, postalCode: true },
      'HK': { state: false, postalCode: false },
      'IN': { state: true, postalCode: true },
      'BR': { state: true, postalCode: true },
      'MX': { state: true, postalCode: true },
      // Default for countries not specifically listed
      'DEFAULT': { state: false, postalCode: true }
    };
    
    return requirements[countryCode] || requirements['DEFAULT'];
  }

  ngOnInit() {
    this.stripe = this.stripeFactory.create(Penmate.env.stripeKey);
    
    // Set up the debounced address change handler
    this.addressChangeSubscription = this.addressChangeSubject
      .debounceTime(this.debounceTime)
      .subscribe(event => {
        if (event) {
          if (!event.value) {
            return;
          }
          const firstName = event.value.firstName;
          const lastName = event.value.lastName;
          let fullName = event.value.name;
          
          if (firstName && lastName && !fullName) {
            fullName = `${firstName} ${lastName}`;
          }
          
          const address = event.value.address;
          const returnAddress: StripeAddressValue = {
            name: fullName,
            firstName,
            lastName,
            address_line1: address.line1,
            address_line2: address.line2,
            address_city: address.city,
            address_state: address.state,
            address_zip: address.postal_code,
            address_country: address.country,
          };
          
          // Custom name validation - require at least 2 characters for each name
          const firstNameValid = !firstName || firstName.trim().length >= 2;
          const lastNameValid = !lastName || lastName.trim().length >= 2;
          const nameValidationPassed = firstNameValid && lastNameValid;
          
          // Set validation error message for template
          this.setNameValidationError(firstNameValid, lastNameValid, firstName, lastName);
          
          // Update cached validity state (include custom name validation)
          this._isValid = event.complete && nameValidationPassed;
          
          this.addressChange.emit({
            complete: event.complete,
            value: returnAddress
          });
          this.cdr.detectChanges();
        }
      });
  }

  ngAfterViewInit() {
    this.initStripeAddressElement();
  }

  ngOnDestroy() {
    this.cleanupStripeElement();
    if (this.addressChangeSubscription) {
      this.addressChangeSubscription.unsubscribe();
    }
  }

  /**
   * Public method to reinitialize the Stripe address element from outside the component
   * @param newDefaultAddress Optional new default address values
   */
  public reinitialize(newDefaultAddress?: StripeAddressValue): void {
    if (newDefaultAddress) {
      this.defaultAddress = newDefaultAddress;
    }
    
    // Reset validity state
    this._isValid = false;
    
    this.cleanupStripeElement();
    this.initStripeAddressElement();
    this.cdr.detectChanges();
  }

  /**
   * Gets the current value and validity status of the address element
   * @returns Promise that resolves to object containing complete status and address value
   */
  public async getValue(): Promise<StripeAddressChangeEvent | null> {
    if (!this.addressElement) {
      return null;
    }

    const elementValue = await this.addressElement.getValue();
    if (!elementValue) {
      return null;
    }

    const firstName = elementValue.value.firstName;
    const lastName = elementValue.value.lastName;
    let fullName = elementValue.value.name;
    
    if (firstName && lastName && !fullName) {
      fullName = `${firstName} ${lastName}`;
    }
    
    const address = elementValue.value.address;
    const returnAddress: StripeAddressValue = {
      name: fullName,
      firstName,
      lastName,
      address_line1: address.line1,
      address_line2: address.line2,
      address_city: address.city,
      address_state: address.state,
      address_zip: address.postal_code,
      address_country: address.country,
    };
    
    // Apply same custom name validation
    const firstNameValid = !firstName || firstName.trim().length >= 2;
    const lastNameValid = !lastName || lastName.trim().length >= 2;
    const nameValidationPassed = firstNameValid && lastNameValid;
    
    return {
      complete: elementValue.complete && nameValidationPassed,
      value: returnAddress
    };
  }

  /**
   * Checks if the current address is valid/complete (async version)
   * @returns Promise that resolves to true if the address is complete, false otherwise
   */
  public async isValidAsync(): Promise<boolean> {
    const value = await this.getValue();
    return value ? value.complete : false;
  }

  /**
   * Checks if the current address is valid/complete (synchronous version)
   * Uses cached validity state from the last change event
   * @returns true if the address is complete, false otherwise
   */
  public isValid(): boolean {
    return this._isValid;
  }

  /**
   * Enhanced validation that considers country-specific requirements
   * @param addressValue The address value to validate
   * @returns Object containing validation status and missing fields
   */
  public validateAddressForCountry(addressValue: StripeAddressValue): {
    isValid: boolean;
    missingFields: string[];
    countryCode: string;
  } {
    const countryCode = addressValue.address_country || addressValue.country || 'US';
    const requirements = this.getCountryRequiredFields(countryCode);
    const missingFields: string[] = [];
    
    // Check required basic fields
    if (!addressValue.name && (!addressValue.firstName || !addressValue.lastName)) {
      missingFields.push('name');
    }
    
    if (!addressValue.address_line1 && !addressValue.line1) {
      missingFields.push('address_line1');
    }
    
    if (!addressValue.address_city && !addressValue.city) {
      missingFields.push('city');
    }
    
    // Check country-specific requirements
    if (requirements.state && !addressValue.address_state && !addressValue.state) {
      missingFields.push('state');
    }
    
    if (requirements.postalCode && !addressValue.address_zip && !addressValue.postal_code) {
      missingFields.push('postal_code');
    }
    
    return {
      isValid: missingFields.length === 0,
      missingFields,
      countryCode
    };
  }

  /**
   * Triggers Stripe validation which will display any errors in the UI
   * This should be called before form submission to show validation errors
   * @returns Promise that resolves when validation is complete
   */
  public async triggerValidation(): Promise<void> {
    if (!this.addressElement) {
      return;
    }
    
    // Calling getValue triggers Stripe's validation and error display
    await this.addressElement.getValue();
  }

  /**
   * Set name validation error message
   */
  private setNameValidationError(firstNameValid: boolean, lastNameValid: boolean, firstName: string, lastName: string): void {
    if (!firstNameValid && !lastNameValid) {
      this.nameValidationError = 'Please enter a complete first and last name';
    } else if (!firstNameValid) {
      this.nameValidationError = 'Please enter a complete first name';
    } else if (!lastNameValid) {
      this.nameValidationError = 'Please enter a complete last name';
    } else {
      this.nameValidationError = '';
    }
  }

  /**
   * Cleans up the existing Stripe element to prevent duplicates
   */
  private cleanupStripeElement(): void {
    if (this.addressElement) {
      this.addressElement.destroy();
      this.addressElement = null;
    }
  }

  /**
   * Initializes the Stripe address element
   */
  public initStripeAddressElement(): void {
    if (!this.stripeAddressElement || !this.stripeAddressElement.nativeElement) {
      return;
    }

    const stripe = this.stripe.getInstance();
    const defaultNameParts = (this.defaultAddress.name || '').split(' ');
    const firstName = this.defaultAddress.firstName || defaultNameParts[0] || '';
    const lastName = this.defaultAddress.lastName || defaultNameParts[1] || '';
    const defaultAddress = this.defaultAddress || {};
    // Optimize display for US users while supporting international
    const isDefaultUS = (defaultAddress.address_country || defaultAddress.country || 'US') === 'US';
    
    this.addressElement = (stripe.elements().create as any)("address", {
      mode: "billing",
      display: { 
        name: 'split',
        firstName: 'ALWAYS',
        lastName: 'ALWAYS',
        // Only always show country if it's not US (keeps US experience clean)
        country: isDefaultUS ? 'AUTO' : 'ALWAYS',
        addressLine1: 'ALWAYS',
        addressLine2: 'AUTO',
        city: 'ALWAYS',
        state: 'AUTO',
        postalCode: 'AUTO'
      },
      allowedCountries: [
        // Based on actual production usage data
        // Top tier: Most common non-US countries
        'US', 'GB', 'DE', 'CA', 'MX', 'FR', 'AU', 'ES', 
        // Second tier: Countries with actual usage
        'PL', 'CH', 'AT', 'DO', 'BR', 'AM', 'TR', 'CO', 'DK', 'EE', 'BE', 'IE', 'KR', 'MW', 'NZ',
        // Common international destinations (keep existing coverage)
        'IT', 'NL', 'PT', 'SE', 'NO', 'FI', 'AR', 'CL', 'IN', 'JP', 'SG', 'HK'
      ],
      fields: {
        phone: 'never',  // Don't show phone field as it's not needed for return addresses
        enforceRequiredFields: 'always',  // Required for validation.enforceRequiredFields
        firstName: 'always',  // Required for validation.firstName
        lastName: 'always'    // Required for validation.lastName
      },
      validation: {
        // Enable enhanced validation for international addresses
        enforceRequiredFields: true
      },
      autocomplete: {
        mode: 'google_maps_api',
        apiKey: Penmate.env.gMapsKey,
        // Optimize for international addresses
        autocompleteOptions: {
          // Include all address types for international support
          types: ['address'],
          // Don't restrict to specific countries - let user select any country
          componentRestrictions: {}
        }
      },
      defaultValues: {
        firstName,
        lastName,
        address: {
          line1: defaultAddress.address_line1 || defaultAddress.line1,
          line2: defaultAddress.address_line2 || defaultAddress.line2,
          city: defaultAddress.address_city || defaultAddress.city,
          state: defaultAddress.address_state || defaultAddress.state,
          postal_code: defaultAddress.address_zip || defaultAddress.postal_code,
          country: defaultAddress.address_country || defaultAddress.country || 'US',
        }
      }
    });

    this.addressElement.mount(this.stripeAddressElement.nativeElement);
    this.addressElement.on('change', (event) => {
      // Instead of directly emitting, push to the subject for debouncing
      this.addressChangeSubject.next(event);
    });
    
    // Add blur event to trigger validation when user clicks outside
    this.addressElement.on('blur', async () => {
      // Trigger validation to show any errors
      await this.triggerValidation();
    });
  }
} 